/**
 * WelfarePage - Staff Welfare & Psychosocial Hazards
 * 
 * Features:
 * - List welfare checks with filtering by status
 * - Create welfare check-ins for employees
 * - Track ongoing wellbeing monitoring
 * - Check-in history and outcomes
 * - Confidential handling of sensitive matters
 */
import React, { useState, useEffect } from 'react';
import api, { formatDate, formatDateTime } from '../lib/api';
import { Card, Spinner, EmptyState, Select } from '../components/shared';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Heart, Plus, User, Calendar, Clock, X, Edit2, CheckCircle, MessageCircle, AlertCircle, Shield } from 'lucide-react';

// Status configuration
const WELFARE_STATUS_CONFIG = {
  ACTIVE: { label: 'Active', color: '#f59e0b', bg: '#fffbeb', icon: Clock },
  MONITORING: { label: 'Monitoring', color: '#3b82f6', bg: '#eff6ff', icon: Heart },
  RESOLVED: { label: 'Resolved', color: '#10b981', bg: '#ecfdf5', icon: CheckCircle },
  CLOSED: { label: 'Closed', color: '#6b7280', bg: '#f3f4f6', icon: CheckCircle },
};

// Status badge
const WelfareStatusBadge = ({ status }) => {
  const config = WELFARE_STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="status-badge" style={{ color: config.color, backgroundColor: config.bg }} data-testid={`welfare-status-${status}`}>
      {config.label}
    </span>
  );
};

// Check-in outcome badge
const OutcomeBadge = ({ outcome }) => {
  const outcomes = {
    IMPROVING: { label: 'Improving', color: '#10b981' },
    STABLE: { label: 'Stable', color: '#3b82f6' },
    NEEDS_ATTENTION: { label: 'Needs Attention', color: '#f59e0b' },
    ESCALATE: { label: 'Escalate', color: '#ef4444' },
    CLOSED: { label: 'Closed', color: '#6b7280' },
  };
  const config = outcomes[outcome] || { label: outcome, color: '#6b7280' };
  return <span className="outcome-badge" style={{ color: config.color }}>{config.label}</span>;
};

// Add/Edit Modal
const WelfareFormModal = ({ isOpen, onClose, onSave, item, users }) => {
  const [form, setForm] = useState({
    subject_name: '',
    reason: '',
    assigned_to: '',
    notes: '',
    is_confidential: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        subject_name: item.subject_name || '',
        reason: item.reason || '',
        assigned_to: item.assigned_to || '',
        notes: item.notes || '',
        is_confidential: item.is_confidential ?? true,
      });
    } else {
      setForm({
        subject_name: '',
        reason: '',
        assigned_to: users[0]?.id || '',
        notes: '',
        is_confidential: true,
      });
    }
  }, [item, users]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject_name || !form.reason) {
      toast.error('Please fill in required fields');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" data-testid="welfare-form-modal">
      <div className="modal-content modal-md">
        <div className="modal-header">
          <h2>{item ? 'Edit Welfare Check' : 'Create Welfare Check'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Confidentiality notice */}
            <div className="confidential-notice" data-testid="confidential-notice">
              <Shield size={20} />
              <span>Welfare records are handled confidentially and access is restricted</span>
            </div>

            <div className="form-grid">
              <div className="form-group form-group-full">
                <label className="form-label required">Subject Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="Employee name"
                  value={form.subject_name}
                  onChange={(e) => setForm({...form, subject_name: e.target.value})}
                  data-testid="welfare-subject-name"
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label required">Reason for Welfare Check</label>
                <textarea 
                  className="form-textarea" 
                  rows={3}
                  placeholder="Describe the concern or reason for initiating this welfare check..."
                  value={form.reason}
                  onChange={(e) => setForm({...form, reason: e.target.value})}
                  data-testid="welfare-reason"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Assigned To</label>
                <select 
                  className="form-select" 
                  value={form.assigned_to}
                  onChange={(e) => setForm({...form, assigned_to: e.target.value})}
                  data-testid="welfare-assigned-to"
                >
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={form.is_confidential}
                    onChange={(e) => setForm({...form, is_confidential: e.target.checked})}
                  />
                  <span>Mark as Confidential</span>
                </label>
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Initial Notes</label>
                <textarea 
                  className="form-textarea" 
                  rows={2}
                  placeholder="Any additional notes..."
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="welfare-save-btn">
              {saving ? 'Saving...' : (item ? 'Update' : 'Create Check')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Check-in Modal
const CheckInModal = ({ isOpen, onClose, onSave, welfareCheck }) => {
  const [form, setForm] = useState({
    notes: '',
    outcome: 'STABLE',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.notes) {
      toast.error('Please add check-in notes');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !welfareCheck) return null;

  return (
    <div className="modal-overlay" data-testid="checkin-modal">
      <div className="modal-content modal-sm">
        <div className="modal-header">
          <h2>Record Check-In</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="checkin-subject">
              <User size={20} />
              <span>{welfareCheck.subject_name}</span>
            </div>
            <div className="form-group">
              <label className="form-label required">Check-In Notes</label>
              <textarea 
                className="form-textarea" 
                rows={4}
                placeholder="Document the welfare check-in interaction..."
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                data-testid="checkin-notes"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Outcome</label>
              <select 
                className="form-select" 
                value={form.outcome}
                onChange={(e) => setForm({...form, outcome: e.target.value})}
                data-testid="checkin-outcome"
              >
                <option value="IMPROVING">Improving</option>
                <option value="STABLE">Stable</option>
                <option value="NEEDS_ATTENTION">Needs Attention</option>
                <option value="ESCALATE">Escalate</option>
                <option value="CLOSED">Close Check</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="checkin-save-btn">
              {saving ? 'Saving...' : 'Record Check-In'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal
const WelfareDetailModal = ({ isOpen, onClose, item, onEdit, onCheckIn }) => {
  if (!isOpen || !item) return null;

  const checkIns = item.check_ins || [];

  return (
    <div className="modal-overlay" data-testid="welfare-detail-modal">
      <div className="modal-content modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            {item.is_confidential && <Shield size={18} className="confidential-icon" />}
            <User size={20} />
            <h2>{item.subject_name}</h2>
            <WelfareStatusBadge status={item.status} />
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-section">
              <h3>Details</h3>
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">{formatDate(item.created_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Assigned To</span>
                <span className="detail-value">{item.assigned_to_name || 'Unassigned'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <WelfareStatusBadge status={item.status} />
              </div>
              <div className="detail-row">
                <span className="detail-label">Check-Ins</span>
                <span className="detail-value">{checkIns.length}</span>
              </div>
            </div>
            
            <div className="detail-section detail-section-full">
              <h3>Reason</h3>
              <p className="detail-text">{item.reason}</p>
            </div>

            {/* Check-in History */}
            <div className="detail-section detail-section-full">
              <h3>Check-In History</h3>
              {checkIns.length === 0 ? (
                <p className="empty-text">No check-ins recorded yet</p>
              ) : (
                <div className="checkin-history">
                  {checkIns.slice().reverse().map((checkin, idx) => (
                    <div key={idx} className="checkin-item">
                      <div className="checkin-header">
                        <span className="checkin-date">{formatDateTime(checkin.timestamp)}</span>
                        {checkin.outcome && <OutcomeBadge outcome={checkin.outcome} />}
                      </div>
                      <p className="checkin-notes">{checkin.notes}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {item.outcome && item.status === 'CLOSED' && (
              <div className="detail-section detail-section-full">
                <h3>Resolution</h3>
                <p className="detail-text">{item.outcome}</p>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {item.status !== 'CLOSED' && (
            <Button variant="outline" onClick={() => onCheckIn(item)} data-testid="welfare-checkin-btn">
              <MessageCircle size={16} /> Record Check-In
            </Button>
          )}
          <Button variant="primary" onClick={() => onEdit(item)} data-testid="welfare-edit-btn">
            <Edit2 size={16} /> Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const WelfarePage = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '' });
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load users
      const usersRes = await api.get('/users');
      setUsers(usersRes.items || []);

      // Load welfare checks
      let url = '/welfare';
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await api.get(url);
      setItems(res.items || []);
    } catch (err) {
      console.error('Failed to load welfare checks:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    if (selectedItem) {
      await api.patch(`/welfare/${selectedItem.id}`, formData);
      toast.success('Welfare check updated');
    } else {
      await api.post('/welfare', formData);
      toast.success('Welfare check created');
    }
    loadData();
    setSelectedItem(null);
  };

  const handleCheckIn = async (checkInData) => {
    try {
      await api.post(`/welfare/${selectedItem.id}/checkin`, checkInData);
      toast.success('Check-in recorded');
      loadData();
    } catch (err) {
      toast.error('Failed to record check-in');
      throw err;
    }
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setShowFormModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowDetailModal(false);
    setShowFormModal(true);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const handleOpenCheckIn = (item) => {
    setSelectedItem(item);
    setShowDetailModal(false);
    setShowCheckInModal(true);
  };

  // Calculate summary stats
  const stats = {
    total: items.length,
    active: items.filter(i => i.status === 'ACTIVE').length,
    monitoring: items.filter(i => i.status === 'MONITORING').length,
    resolved: items.filter(i => ['RESOLVED', 'CLOSED'].includes(i.status)).length,
  };

  return (
    <div className="page welfare-page" data-testid="welfare-page">
      <div className="page-header">
        <div>
          <h1>Staff Welfare</h1>
          <p>Monitor employee wellbeing and psychosocial concerns</p>
        </div>
        <Button variant="primary" onClick={handleAdd} data-testid="add-welfare-btn">
          <Plus size={16} /> New Welfare Check
        </Button>
      </div>

      {/* Confidentiality Banner */}
      <div className="confidential-banner" data-testid="welfare-confidential-banner">
        <Shield size={18} />
        <span>Welfare records are confidential. Access is restricted to authorized personnel only.</span>
      </div>

      {/* Summary Stats */}
      <div className="page-stats" data-testid="welfare-stats">
        <div className="stat-card">
          <Heart size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
        <div className={`stat-card ${stats.active > 0 ? 'stat-warning' : ''}`}>
          <AlertCircle size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Active</span>
          </div>
        </div>
        <div className="stat-card">
          <Clock size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.monitoring}</span>
            <span className="stat-label">Monitoring</span>
          </div>
        </div>
        <div className="stat-card stat-success">
          <CheckCircle size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.resolved}</span>
            <span className="stat-label">Resolved</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters-row">
          <Select
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            options={[
              { value: '', label: 'All Statuses' },
              ...Object.entries(WELFARE_STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
          {filters.status && (
            <button className="filter-clear-btn" onClick={() => setFilters({ status: '' })}>
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Items List */}
      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Heart size={48} />}
          title="No welfare checks found"
          description={filters.status ? "No checks match the current filter" : "No welfare checks have been created"}
        />
      ) : (
        <Card className="table-card">
          <table className="data-table" data-testid="welfare-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Reason</th>
                <th>Assigned To</th>
                <th>Check-Ins</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr 
                  key={item.id} 
                  onClick={() => handleView(item)}
                  className="table-row clickable"
                  data-testid={`welfare-row-${item.id}`}
                >
                  <td className="cell-subject">
                    {item.is_confidential && <Shield size={14} className="confidential-icon-small" />}
                    <User size={16} />
                    <span>{item.subject_name}</span>
                  </td>
                  <td className="cell-truncate">{item.reason}</td>
                  <td>{item.assigned_to_name || '-'}</td>
                  <td>{(item.check_ins || []).length}</td>
                  <td><WelfareStatusBadge status={item.status} /></td>
                  <td className="cell-date">{formatDate(item.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modals */}
      <WelfareFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setSelectedItem(null); }}
        onSave={handleSave}
        item={selectedItem}
        users={users}
      />
      <WelfareDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedItem(null); }}
        item={selectedItem}
        onEdit={handleEdit}
        onCheckIn={handleOpenCheckIn}
      />
      <CheckInModal
        isOpen={showCheckInModal}
        onClose={() => { setShowCheckInModal(false); }}
        onSave={handleCheckIn}
        welfareCheck={selectedItem}
      />
    </div>
  );
};

export default WelfarePage;
